import { useContext } from "react";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { listRescheduleOffersMeRescheduleOffersGet } from "@/__generated__/api/me/me";
import type { ActionableRescheduleOfferOut } from "@/__generated__/api/wrenAPI.schemas";
import type { QueryShape } from "../meHooks";

export const ME_RESCHEDULE_OFFERS_QUERY_KEY = ["me", "reschedule-offers"] as const;

// Mirror sessionHooks: fall back to a detached client when no provider is
// mounted, so the hub's data hooks render in detached contexts (routing/gating
// tests) without a QueryClientProvider. The real app supplies MeApp's client.
const detachedQueryClient = new QueryClient();

function useQueryClientSafe(): QueryClient {
  return useContext(QueryClientContext) ?? detachedQueryClient;
}

/**
 * The signed-in person's actionable pending reschedule offers (`GET
 * /me/reschedule-offers`), soonest deadline first.
 *
 * This read is the reschedule resilience surface: a durable offer exists in the
 * DB regardless of whether the best-effort notification reached either partner,
 * so Homebase resolves it here to render an actionable CTA (see
 * `RescheduleOfferCtas`) — the offer is discoverable without ever opening the
 * notification deeplink.
 *
 * The generated client throws on non-2xx (customInstance), so a resolved
 * response is the success payload; `res.data` is the offer list.
 */
export function useActionableRescheduleOffers(
  opts: { enabled?: boolean } = {},
): QueryShape<ActionableRescheduleOfferOut[]> {
  const { enabled = true } = opts;
  const queryClient = useQueryClientSafe();
  const query = useQuery(
    {
      queryKey: ME_RESCHEDULE_OFFERS_QUERY_KEY,
      enabled,
      queryFn: async () => {
        const res = await listRescheduleOffersMeRescheduleOffersGet();
        // customInstance throws on non-2xx, so a resolved response is the success
        // branch of the orval union (whose `data` includes the 422 error shape).
        return res.data as ActionableRescheduleOfferOut[];
      },
    },
    queryClient,
  );
  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}
